/* SECURITY: This file does nothing unsafe with localStorage */
import { useCallback, useEffect, useState } from 'react';

import { useAccount } from '@hcs/authn';
import { createCustomErrorWithOriginal, logException } from '@hcs/utils';

const LocalStorageStateError = createCustomErrorWithOriginal(
  'LocalStorageStateError',
);

const callbacks: Record<string, () => void> = {};
const getState = <StateValueType>(
  localStorageKey: string,
): StateValueType | null => {
  const value = localStorage.getItem(localStorageKey);
  let parsedValue = null;
  try {
    if (value) {
      parsedValue = JSON.parse(value);
    }
  } catch (e) {
    logException(
      new LocalStorageStateError(
        `Failed to parse localStorage item for key "${localStorageKey}"`,
        e,
      ),
    );
  }
  return parsedValue;
};

/** A localStorage state hook that will trigger renders when the state changes */
export const useLocalStorageState = <
  StateValueType = Record<string, string | null | undefined>,
>(
  key: string,
) => {
  const { data: account } = useAccount();
  const localStorageKey = `${key}==${account?.user.id || 'noauth'}`;
  const [state, setState] = useState(getState<StateValueType>(localStorageKey));
  const dispatchStateChangeEvent = useCallback(() => {
    document.dispatchEvent(new Event(localStorageKey));
  }, [localStorageKey]);

  useEffect(() => {
    setState(getState<StateValueType>(localStorageKey));
    const handleLocalStorageChange = () => {
      setState(getState<StateValueType>(localStorageKey));
    };
    // Subscribe hook instance to listeners
    callbacks[localStorageKey] = handleLocalStorageChange;
    document.addEventListener(localStorageKey, handleLocalStorageChange, false);
    return () => {
      if (callbacks[localStorageKey]) {
        delete callbacks[localStorageKey];
        document.removeEventListener(localStorageKey, handleLocalStorageChange);
      }
    };
  }, [localStorageKey]);

  return {
    state,
    actions: {
      clear: useCallback(() => {
        localStorage.removeItem(localStorageKey);
        dispatchStateChangeEvent();
      }, [localStorageKey, dispatchStateChangeEvent]),
      clearKey: useCallback(
        <K extends keyof StateValueType>(key: K) => {
          if (state) {
            const newState = { ...state };
            delete newState[key];
            localStorage.setItem(localStorageKey, JSON.stringify(newState));
            dispatchStateChangeEvent();
          }
        },
        [localStorageKey, state, dispatchStateChangeEvent],
      ),
      clearKeys: useCallback(
        <K extends keyof StateValueType>(keys: K[]) => {
          if (state) {
            const newState = { ...state };
            keys.forEach((key) => {
              delete newState[key];
            });
            localStorage.setItem(localStorageKey, JSON.stringify(newState));
            dispatchStateChangeEvent();
          }
        },
        [localStorageKey, state, dispatchStateChangeEvent],
      ),
      setKeyValue: useCallback(
        <K extends keyof StateValueType>(key: K, value: StateValueType[K]) => {
          const newState = {
            ...state,
            [key]: value,
          };
          localStorage.setItem(localStorageKey, JSON.stringify(newState));
          dispatchStateChangeEvent();
        },
        [localStorageKey, state, dispatchStateChangeEvent],
      ),
      setValue: useCallback(
        (value: StateValueType) => {
          localStorage.setItem(localStorageKey, JSON.stringify(value));
          dispatchStateChangeEvent();
        },
        [localStorageKey, dispatchStateChangeEvent],
      ),
    },
  };
};
